import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faSignature, faSpinner } from '@fortawesome/free-solid-svg-icons';
import '../styles/Login.css';
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  updateProfile, 
  sendEmailVerification,
  sendPasswordResetEmail // Added for password reset
} from 'firebase/auth';
import { auth } from '../firebase';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [authView, setAuthView] = useState<'login' | 'signup' | 'forgotPassword'>('login'); // New state for view management
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setErrorMessage(''); // Clear error messages
    setPassword(''); // Clear password field
    // Optionally, clear other fields like firstName, lastName if switching from signup
    if (authView === 'login' || authView === 'signup') {
      // Only clear names if not in signup or moving away from it
      // setFirstName('');
      // setLastName('');
    }
  }, [authView]); // Updated dependency
  
  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (parallaxRef.current) {
        const x = (window.innerWidth / 2 - e.clientX) / 30;
        const y = (window.innerHeight / 2 - e.clientY) / 30;
        parallaxRef.current.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrorMessage('');
    setIsLoading(true);
    try {
      if (authView === 'signup') { // Changed from isSignUp
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await updateProfile(user, { displayName: `${firstName} ${lastName}`.trim() });
        
        await sendEmailVerification(user);
        setErrorMessage('Verification email sent! Please check your inbox and verify your email before logging in.');
        setIsLoading(false); // Stop loading as the next step is user action (verification)
        return; // Exit handleSubmit, do not call onLogin()
      } else {
        // Login flow
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        if (!user.emailVerified) {
          setErrorMessage('Please verify your email before logging in. Check your inbox. If you just signed up, please verify your email first.');
          setIsLoading(false); // Stop loading
          return; // Prevent login
        }
        onLogin(); // Proceed to login only if email is verified
      }
    } catch (error: any) {
      if (error.code === 'auth/invalid-credential') {
        setErrorMessage('Wrong email or password. Please verify and try again.');
      } else {
        setErrorMessage(error.message || 'An unexpected error occurred');
      }
      setIsLoading(false); // Ensure loading is stopped on error
    }
    // Removed finally block for setIsLoading(false) as it's handled in each branch
  };

  const handleSignInWithGoogle = async () => {
    setErrorMessage('');
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      onLogin();
    } catch (error: any) {
      setErrorMessage(error.message || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address to reset your password.');
      return;
    }
    setIsLoading(true);
    setErrorMessage(''); 
    try {
      await sendPasswordResetEmail(auth, email);
      setErrorMessage(`Password reset email sent to ${email}. Please check your inbox.`);
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setErrorMessage('No user found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('The email address is not valid.');
      } else {
        setErrorMessage(error.message || 'Failed to send password reset email. Please try again.');
      }
    }
    setIsLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderLoginSignUpFields = () => { // Renamed from renderFormFields and adjusted
    return (
      <>
        {authView === 'signup' && ( // Changed from isSignUp
          <>
            <div className="form-group">
              <label htmlFor="firstName"><FontAwesomeIcon icon={faSignature} /></label>
              <input type="text" id="firstName" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="lastName"><FontAwesomeIcon icon={faSignature} /></label>
              <input type="text" id="lastName" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
          </>
        )}
        <div className="form-group">
          <label htmlFor="email"><FontAwesomeIcon icon={faUser} /></label>
          <input type="email" id="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="form-group">
          <label htmlFor="password"><FontAwesomeIcon icon={faLock} /></label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
        </div>
        <div className="password-options">
          {authView === 'login' && ( // Only show "Forgot password?" in login view
            <button 
              type="button" 
              onClick={() => { setAuthView('forgotPassword'); setErrorMessage(''); }} 
              className="forgot-password-link"
            >
              Forgot password?
            </button>
          )}
          <div className="show-password-checkbox">
            <input
              type="checkbox"
              id="show-password-checkbox"
              checked={showPassword}
              onChange={togglePasswordVisibility}
            />
            <label htmlFor="show-password-checkbox">Show password</label>
          </div>
        </div>

        <button type="submit" className="login-button" disabled={isLoading}>
          {isLoading ? (<><FontAwesomeIcon icon={faSpinner} spin /> {authView === 'signup' ? ' Creating Account...' : ' Logging in...'}</>) : (authView === 'signup' ? 'Sign Up' : 'Login')}
        </button>
      </>
    );
  };

  const renderForgotPasswordView = () => {
    return (
      <>
        <h2>Reset Your Password</h2>
        <p className="login-subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }}>
          <div className="form-group">
            <label htmlFor="email-forgot"><FontAwesomeIcon icon={faUser} /></label>
            <input
              type="email"
              id="email-forgot" 
              placeholder="Email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? <><FontAwesomeIcon icon={faSpinner} spin /> Sending Link...</> : 'Send Password Reset Link'}
          </button>
        </form>
        <div className="login-switch">
          <p><button className="link-button" onClick={() => { setAuthView('login'); setErrorMessage(''); }}>Back to Login</button></p>
        </div>
      </>
    );
  };

  return (
    <div className="login-page-wrapper">
      <div className="parallax-logo" ref={parallaxRef}></div>
      
      <div className="login-container">
        <div className="login-content">
          <div className="login-logo">
            <img src="./clai_editor_logo.png" alt="CLAI Logo" />
          </div>
          
          <div className="login-form-container">
            {authView === 'forgotPassword' ? (
              renderForgotPasswordView()
            ) : (
              <>
                <h2>{authView === 'signup' ? 'Create an Account' : 'Welcome Back'}</h2>
                <p className="login-subtitle">
                  {authView === 'signup' 
                    ? 'Create a new account to get started with CLAI' 
                    : 'Log in to access your CLAI dashboard'}
                </p>
                
                {errorMessage && ( // This error message is for login/signup
                  <div className="error-message">
                    {errorMessage}
                  </div>
                )}
                
                <form className="login-form" onSubmit={handleSubmit}>
                  {renderLoginSignUpFields()} 
                </form>
                
                <div className="or-divider"><span>or</span></div>
                <button type="button" className="google-login-button" onClick={handleSignInWithGoogle} disabled={isLoading} aria-label="Sign in with Google">
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" style={{ width: 20, marginRight: 8, verticalAlign: 'middle' }} />
                  Sign in with Google
                </button>
                
                <div className="login-switch">
                  {authView === 'signup' ? (
                    <p>Already have an account? <button className="link-button" onClick={() => { setAuthView('login'); setErrorMessage(''); }}>Log in</button></p>
                  ) : (
                    <p>Don't have an account? <button className="link-button" onClick={() => { setAuthView('signup'); setErrorMessage(''); }}>Sign up</button></p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;